import { Column, Table } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import DropdownMenuButton from '../drop-downs/dropdown-menu-button';
import { multiSelectFilter } from './table-functions/filters';
import { SearchFilterOptions } from './tableTypes';
import { useFilteredValues } from 'common/hooks/filteredValues';
import DropdownMenuReactPortal from '../drop-downs/drop-down-menu-react-portal';

export interface TableFilterToggleProps {
  column: Column<any>;
  componentId: string;
  table: Table<any>;
  isLastColumn?: boolean;
}

const TableFilterToggle = ({
  column,
  componentId,
  table,
  isLastColumn
}: TableFilterToggleProps) => {
  const preFilteredValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const facetedUniqueValues = column.getFacetedUniqueValues();

  const isMultiSelectFilter = column.columnDef.filterFn === multiSelectFilter;

  const sortedUniqueValues = useMemo(
    () =>
      typeof preFilteredValue === 'number'
        ? []
        : Array.from(facetedUniqueValues.keys()).sort((a, b) =>
            a.localeCompare(b)
          ),
    [preFilteredValue, facetedUniqueValues]
  );

  const [searchTerm, setSearchTerm] = useState('');

  const isSearchFilterEnabled = (meta: any): boolean => {
    const searchFilterOptions: SearchFilterOptions | undefined =
      meta?.searchFilterOptions;
    if (searchFilterOptions === undefined) {
      return false;
    }
    return searchFilterOptions?.isEnabled;
  };

  const filteredUniqueValues = useFilteredValues(
    sortedUniqueValues,
    searchTerm,
    (value, term) => value.toLowerCase().indexOf(term) !== -1
  );

  const typedColumnFilterValue = isMultiSelectFilter
    ? (column.getFilterValue() as string[])
    : (column.getFilterValue() as string);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const triggerFilter = (isFiltered: boolean, value: any) => {
    if (isMultiSelectFilter) {
      let multiSelectedColumns = typedColumnFilterValue as string[] | undefined;
      if (multiSelectedColumns === undefined) {
        multiSelectedColumns = [];
      }
      const updatedFilter = isFiltered
        ? multiSelectedColumns.filter((v) => v !== value)
        : [...multiSelectedColumns, value];

      column.setFilterValue(updatedFilter);
    } else {
      column.setFilterValue(isFiltered ? '' : value);
    }
  };

  const isValueFiltered = (value: any, filterValues: string | string[]) => {
    if (Array.isArray(filterValues)) {
      return filterValues.includes(value);
    } else {
      return filterValues === value;
    }
  };

  return (
    <DropdownMenuReactPortal
      isDropdownOpen={isDropdownOpen}
      buttonClasses={`rounded md:px-2 ${
        typedColumnFilterValue ? 'bg-neutral-100' : ''
      } hover:bg-neutral-100 flex items-center`}
      buttonIconClasses="ra-icons-generic-filter text-primary-700 flex over"
      dropdownMenuClasses={`z-40 ${
        isLastColumn ? 'right-0' : 'left-0'
      } w-44 top-7 overflow-visible`}
      buttonLabel={
        isMultiSelectFilter
          ? `${getHeaderName(column) ?? column.id ?? ''} (${
              (column.getFilterValue() as string[])?.length || 0
            })`
          : getHeaderName(column) ?? column.id ?? ''
      }
      onDropdownClicked={() => setIsDropdownOpen(!isDropdownOpen)}
      onClickAway={() => setIsDropdownOpen(false)}
    >
      {isSearchFilterEnabled(column.columnDef.meta) ? (
        <div className="relative p-2">
          <input
            className="w-full p-2 pl-4 rounded border border-neutral-200 text-sm"
            type="search"
            placeholder="Contains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-4 flex items-center text-navy bg-transparent"
              onClick={(e) => {
                e.preventDefault();
                setSearchTerm('');
              }}
            >
              <i className="ra-icons-generic-cross-1 text-primary-700 flex"></i>
            </button>
          )}
        </div>
      ) : (
        <></>
      )}
      <>
        {filteredUniqueValues.map((value) => {
          const isFiltered = isValueFiltered(value, typedColumnFilterValue);
          return (
            <DropdownMenuButton
              key={`${componentId}-${column.id}-${value}`}
              identifier={`${componentId}-${column.id}-${value}`}
              buttonClasses={`${
                isFiltered ? 'bg-neutral-200' : ''
              } hover:bg-neutral-200 overflow-x-auto text-sm`}
              onClick={() => triggerFilter(isFiltered, value)}
            >
              {value}
            </DropdownMenuButton>
          );
        })}
      </>
    </DropdownMenuReactPortal>
  );
};

export default TableFilterToggle;

function getHeaderName(column: Column<any, unknown>): string | undefined {
  return column.columnDef.header?.toString()?.split("'")[1];
}
